import { ToolMetadata, ResourceMetadata, ToolOptions, ResourceOptions } from './types.js';

/**
 * Registry for managing tool and resource metadata
 * This is a singleton per BeastMcpServer instance
 */
export class BeastMcpRegistry {
  private tools = new Map<string, ToolMetadata>();
  private resources = new Map<string, ResourceMetadata>();

  /**
   * Register a tool with the registry
   */
  registerTool(id: string, options: ToolOptions, handler: (args: any, context: any) => Promise<any>): void {
    if (this.tools.has(id)) {
      throw new Error(`Tool with id '${id}' is already registered`);
    }

    this.tools.set(id, {
      id,
      options,
      handler
    });
  }

  /**
   * Register a resource with the registry
   */
  registerResource(id: string, options: ResourceOptions, handler: (context: any) => Promise<any>): void {
    if (this.resources.has(id)) {
      throw new Error(`Resource with id '${id}' is already registered`);
    }

    this.resources.set(id, {
      id,
      options,
      handler
    });
  }

  /**
   * Get a tool by ID
   */
  getTool(id: string): ToolMetadata | undefined {
    return this.tools.get(id);
  }

  /**
   * Get a resource by ID
   */
  getResource(id: string): ResourceMetadata | undefined {
    return this.resources.get(id);
  }

  /**
   * Get all registered tools
   */
  getAllTools(): ToolMetadata[] {
    return Array.from(this.tools.values());
  }

  /**
   * Get all registered resources
   */
  getAllResources(): ResourceMetadata[] {
    return Array.from(this.resources.values());
  }

  /**
   * Check if a tool is registered
   */
  hasTool(id: string): boolean {
    return this.tools.has(id);
  }

  /**
   * Check if a resource is registered
   */
  hasResource(id: string): boolean {
    return this.resources.has(id);
  }

  /**
   * Unregister a tool
   */
  unregisterTool(id: string): boolean {
    return this.tools.delete(id);
  }

  /**
   * Unregister a resource
   */
  unregisterResource(id: string): boolean {
    return this.resources.delete(id);
  }

  /**
   * Clear all tools and resources
   */
  clear(): void {
    this.tools.clear();
    this.resources.clear();
  }

  /**
   * Get the number of registered tools
   */
  getToolCount(): number {
    return this.tools.size;
  }

  /**
   * Get the number of registered resources
   */
  getResourceCount(): number {
    return this.resources.size;
  }
} 